import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface ContradictionEvidence {
  id: string; source_label: string | null; source_evidence_id: string | null;
  chunk_id: string | null; occurred_on: string | null; quote: string | null;
  thrust: string | null; strength: string | null;
}
export interface SubFact {
  assertion: string; verdict: string; severity: string | null; rationale: string | null;
  contradiction_type: string | null; proposer_verdict: string | null; judge_verdict: string | null;
  review_status: string | null; evidence: Omit<ContradictionEvidence, "id">[];
}
export interface Contradiction {
  id: string; paragraph_number: number | null; allegation_text: string | null;
  qualifier: string | null; verdict: string; contradiction_type: string | null; severity: string | null;
  rationale: string | null; confidence: string | null; section_id: string | null; review_status: string | null;
  needs_review: boolean | null;
  human_assessment: string | null;
  human_note: string | null;
  proposer_verdict: string | null; proposer_rationale: string | null;
  judge_verdict: string | null; judge_rationale: string | null;
  sub_facts: SubFact[] | null; evidence: ContradictionEvidence[];
}

export function useCaseContradictions(caseId: string | undefined) {
  return useQuery<Contradiction[], Error>({
    queryKey: ["case-contradictions", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!caseId) return [];
      const { data, error } = await berserkr().rpc("fetch_case_contradictions", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as Contradiction[]) ?? [];
    },
  });
}

/** Record the human's accuracy call on one allegation; optimistic so the card flips instantly. */
export function useSetContradictionAssessment(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; assessment: string; note?: string }, { prev?: Contradiction[] }>({
    mutationFn: async (args) => {
      const { error } = await berserkr().rpc("set_contradiction_assessment", {
        p_id: args.id, p_assessment: args.assessment, p_note: args.note ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: ["case-contradictions", caseId] });
      const prev = qc.getQueryData<Contradiction[]>(["case-contradictions", caseId]);
      qc.setQueryData<Contradiction[]>(["case-contradictions", caseId], (old) =>
        (old ?? []).map((r) =>
          r.id === args.id
            ? { ...r, human_assessment: args.assessment, needs_review: false, human_note: args.note ?? r.human_note }
            : r));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["case-contradictions", caseId], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["case-contradictions", caseId] }),
  });
}
